import {Box, Button, Typography} from '@mui/material';
import {t} from 'i18next';
import {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {getEcaFields} from '../../../api/EcaServices';
import {
  ISortOrder,
  setColumnsList,
  setSortOrder,
} from '../../../redux/slices/ecaDocListSlice';
import AurButton from '../../common/aur-button/AurButton';
import AurDrawer from '../../common/aur-drawer/AurDrawer';
import AurTabMenu, {AurTab} from '../../common/aur-tab-menu/AurTabMenu';
import {IFieldModal} from '../../pages/field-management/custom-field/constant';
import {
  ArrayItemType,
  getDefaultDocListColumns,
  SortItemType,
} from '../eca-doc-list.util';
import ManageColFields from './ManageColFields';
import ManageSortOrder from './ManageSortOrder';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';

const FieldsSortDrawer = ({
  showDrawer,
  handleDrawer,
  clientId,
  projectId,
  currentColumns,
  sortOrder,
  isNlpSearch = false,
}: {
  showDrawer: boolean;
  handleDrawer: (flag: boolean) => void;
  clientId: string;
  projectId: string;
  currentColumns: ArrayItemType[];
  sortOrder: ISortOrder[];
  isNlpSearch?: boolean;
}) => {
  const [selectedList, setSelectedList] = useState<ArrayItemType[]>(
    currentColumns.map((col, index) => ({...col, id: 'right-' + index}))
  );
  const [sortList, setSortList] = useState<SortItemType[]>([]);
  const [allColumns, setAllColumns] = useState<ArrayItemType[]>([]);
  const dispatch = useDispatch();
  const getSelectedList = (list: ArrayItemType[]) => setSelectedList(list);
  const getSortOrder = (list: SortItemType[]) => setSortList(list);
  const filteredSelectedList = selectedList ?? [];
  const {trackGAEvent} = useAnalytics();

  const tabs: AurTab[] = [
    {
      header: {
        text: t('indexSearchPage.aurTabControl.fields'),
      },
      content:
        allColumns.length > 0 ? (
          <ManageColFields
            getSelectedList={getSelectedList}
            selectedList={selectedList}
            availableList={allColumns}
          />
        ) : (
          <></>
        ),
      index: 0,
    },
    {
      header: {
        text: t('indexSearchPage.aurTabControl.sortOrder'),
      },
      content: (
        <ManageSortOrder
          selectedList={filteredSelectedList.filter((i) => i.isSortable)}
          sortOrder={sortList}
          getSortOrder={getSortOrder}
        />
      ),
      index: 1,
    },
  ];

  useEffect(() => {
    const params = `clientId=${clientId}&projectId=${projectId}&top=10000`;
    const getMetadata = async () => {
      try {
        const results = await getEcaFields(params);

        const allFields = (results?.data?.data || []).filter(
          (item: IFieldModal) =>
            item.fieldCategory == 'STANDARD_METADATA' ||
            item.fieldCategory == 'CUSTOM_METADATA' ||
            item.fieldCategory == 'TAG_FIELD'
        );

        const resultsIds = allFields.map((col: IFieldModal, index: number) => ({
          name: col.fieldName,
          label: col.fieldLabel,
          dataType: col.fieldESType?.toLowerCase(),
          UIDataType: col.fieldUIType?.toLowerCase(),
          id: 'left' + index,
          fieldId: col.fieldId,
          isSortable: col.isSortable,
        }));
        setAllColumns(resultsIds);
      } catch (e) {
        setAllColumns(
          getDefaultDocListColumns().map((col, index) => ({
            ...col,
            id: 'left' + index,
          }))
        );
      }
    };
    if (clientId && projectId) {
      getMetadata();
    }
  }, []);

  useEffect(() => {
    const prevOrder = sortOrder.map((order, index) => ({
      fieldValue: {
        label: order.label,
        id: order.name,
        name: order.name,
        dataType: order.dataType,
        UIDataType: order.UIDataType ?? '',
      },
      sortValue: {
        label: order.direction.toUpperCase(),
        id: order.direction == 'asc' ? '1' : '2',
        name: order.direction,
        dataType: 'string',
      },
      id: index,
    }));
    const totalLength = 5;
    const remainingElementsCount = totalLength - prevOrder.length;
    const updatedArray = [
      ...prevOrder,
      ...new Array(remainingElementsCount).fill(null).map((_, index) => ({
        fieldValue: {
          label: 'Choose',
          id: '0',
          name: 'choose',
          dataType: '',
        },
        sortValue: {
          label: 'ASC',
          id: '1',
          name: 'asc',
          dataType: '',
        },
        id: prevOrder.length + index,
      })),
    ];

    setSortList(
      updatedArray.filter((sortItem) =>
        selectedList.some(
          (colItem) =>
            colItem.name == sortItem.fieldValue.name ||
            sortItem.fieldValue.name == 'choose'
        )
      )
    );
  }, [selectedList]);

  useEffect(() => {
    !showDrawer && setSelectedList([]);
  }, [showDrawer]);

  const handleClose = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.ecaDocList.dontSaveChanges'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.DRAWER,
        action: `${t('gaTracking.ecaDocList.canceledChangeSettings')}`,
      },
      subPage: {
        subpage_name: t('gaTracking.ecaDocList.manageColumsSortOrder'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
    handleDrawer(false);
  };

  const contentBody = (
    <Box className="manage-field-sort-panel">
      <Box className="panel-header">
        <Box className="close-btn-container">
          <AurButton
            classNames={'drawer-close-button panel-close'}
            label={t('buttons.close')}
            type="exit"
            size="Medium"
            title={t('buttons.close')}
            onClick={() => handleClose()}
          />
        </Box>
        <Box className="header-content">
          <Typography className="typo-text headline1_size title">
            {t('ecaDocList.manageFieldsSort.title')}
          </Typography>
          <Box>
            <AurButton
              classNames={'drawer-close-button panel-close'}
              label={t('buttons.dontSaveUpdate')}
              type="exit"
              size="Medium"
              title={t('buttons.dontSaveUpdate')}
              onClick={() => handleClose()}
            />
            <Button
              id="basic-button"
              aria-describedby={'save'}
              onClick={() => {
                trackGAEvent({
                  eventName: GA_EVENT_NAMES.CLICK,
                  click: {
                    name: t('gaTracking.ecaDocList.saveChanges'),
                    type: GA_CLICK_TYPES.BUTTON,
                    region: GA_REGIONS.DRAWER,
                    action: `${t('gaTracking.ecaDocList.savedChanges')}`,
                  },
                  subPage: {
                    subpage_name: t(
                      'gaTracking.ecaDocList.manageColumsSortOrder'
                    ),
                    subpage_type: GA_SUBPAGE_TYPE.DRAWER,
                  },
                });
                const newSortArray = sortList
                  .filter(
                    (item) =>
                      item.fieldValue.name && item.fieldValue.name != 'choose'
                  )
                  .map((item) => ({
                    column: item.fieldValue.name,
                    direction: item.sortValue.name,
                    name: item.fieldValue.name,
                    label: item.fieldValue.label,
                    dataType: item.fieldValue.dataType,
                    UIDataType: item.fieldValue.UIDataType,
                  }));

                dispatch(setSortOrder({isNlpSearch, sortOrder: newSortArray}));
                dispatch(
                  setColumnsList({
                    isNlpSearch,
                    columns:
                      selectedList.length > 0
                        ? selectedList
                        : getDefaultDocListColumns(),
                  })
                );
                handleDrawer(false);
              }}
              className="typo-text buttonMedium_size custom-menu-button custom-button"
              variant="contained"
            >
              {t('buttons.save')}
            </Button>
          </Box>
        </Box>
      </Box>
      <Box className="manageColSort-content">
        <Box className="tab-container">
          <AurTabMenu
            tabs={tabs}
            headerClasses="typo-text subhead4_size"
            isResponsive={true}
            width="100%"
            onTabChange={(newValue) => {
              trackGAEvent({
                eventName: GA_EVENT_NAMES.CLICK,
                click: {
                  name: tabs[newValue].header.text,
                  type: GA_CLICK_TYPES.TAB,
                  region: GA_REGIONS.DRAWER,
                  action:
                    newValue == 0
                      ? t('gaTracking.ecaDocList.tabFields')
                      : t('gaTracking.ecaDocList.tabSortOrder'),
                },
                subPage: {
                  subpage_name: t(
                    'gaTracking.ecaDocList.manageColumsSortOrder'
                  ),
                  subpage_type: GA_SUBPAGE_TYPE.DRAWER,
                },
              });
            }}
          />
        </Box>
      </Box>
    </Box>
  );
  return (
    <AurDrawer
      className="manage-field-sort-drawer"
      open={showDrawer}
      onClose={() => handleDrawer(false)}
      anchor="right"
      drawerContent={contentBody}
    />
  );
};

export default FieldsSortDrawer;
